import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../components/AuthContext.jsx";
import { api } from "../api/config.js";

import {
  faCreditCard,
  faGlobe,
  faLock,
  faCheck,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const Billing = () => {
  const { authToken } = useAuth();

  // Payment methods data
  const paymentMethods = [
    // International Cards
    { id: "visa", name: "Visa", category: "International Cards", icon: "💳" },
    {
      id: "mastercard",
      name: "Mastercard",
      category: "International Cards",
      icon: "💳",
    },
    {
      id: "unionpay",
      name: "UnionPay",
      category: "International Cards",
      icon: "💳",
    },
    { id: "jcb", name: "JCB", category: "International Cards", icon: "💳" },
    // Domestic Card
    { id: "mpu", name: "MPU", category: "Domestic Card", icon: "💳" },
    // Mobile Wallets
    { id: "kbzpay", name: "KBZPay", category: "Mobile Wallets", icon: "📱" },
    { id: "wavepay", name: "WavePay", category: "Mobile Wallets", icon: "📱" },
    { id: "ayapay", name: "AYA Pay", category: "Mobile Wallets", icon: "📱" },
    { id: "cbpay", name: "CB Pay", category: "Mobile Wallets", icon: "📱" },
  ];

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [methodDropdownOpen, setMethodDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef(null);
  const methodDropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    amount: 30000,
    currency: "mmk",
    orderType: "App\\Models\\PointGift",
    orderId: 10,
    redirect_url: `${window.location.origin}/billingSuccess`,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [paymentPayload, setPaymentPayload] = useState(null);

  // Read payment amount from localStorage when component mounts
  useEffect(() => {
    const paymentAmount = localStorage.getItem("paymentAmount");
    const paymentAddonType = localStorage.getItem("paymentAddonType");
    const paymentAddonTypeId = localStorage.getItem("paymentAddonTypeId");

    if (paymentAmount) {
      setFormData((prev) => ({
        ...prev,
        amount: parseInt(paymentAmount) || prev.amount,
      }));
    }

    // Set order type and ID based on addon type
    if (paymentAddonType === "stampPoints") {
      setFormData((prev) => ({
        ...prev,
        orderType: "App\\Models\\Addon",
        orderId: paymentAddonTypeId ? parseInt(paymentAddonTypeId) : 5,
      }));
    } else if (paymentAddonType === "additionalUser") {
      setFormData((prev) => ({
        ...prev,
        orderType: "App\\Models\\Addon",
        orderId: paymentAddonTypeId ? parseInt(paymentAddonTypeId) : 6,
      }));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedPaymentMethod) {
      setError("Please select a payment method");
      return;
    }

    setLoading(true);
    setError("");
    setPaymentPayload(null);

    try {
      const response = await api.post(
        "/payment/create",
        {
          ...formData,
          paymentMethod: selectedPaymentMethod,
          redirect_url: `${window.location.origin}/billingSuccess`,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      const data = response.data;
      console.log("res data:", data);

      if (response.status !== 200) {
        throw new Error(data.error || "Payment creation failed");
      }

      // Store transaction details in localStorage for success page
      const transactionDetails = {
        orderId: data.orderId || formData.orderId,
        amount: formData.amount,
        currency: formData.currency,
        paymentMethod: selectedPaymentMethod,
        transactionId:
          data.transactionId || data.orderId || `TXN-${Date.now()}`,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem(
        "transactionDetails",
        JSON.stringify(transactionDetails),
      );

      // Store the payment payload for CyberSource submission
      setPaymentPayload(data);
    } catch (err) {
      setError(err.message || "An error occurred");
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "amount" || name === "orderId" ? parseInt(value) || 0 : value,
    }));
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
        setCategoryDropdownOpen(false);
      }
      if (
        methodDropdownRef.current &&
        !methodDropdownRef.current.contains(event.target)
      ) {
        setMethodDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Define payment categories
  const paymentCategories = [
    { id: "international", name: "International Cards", icon: "💳" },
    { id: "domestic", name: "Domestic Card", icon: "💳" },
    { id: "mobile", name: "Mobile Wallets", icon: "📱" },
  ];

  // Group payment methods by category
  const groupedMethods = {
    "International Cards": paymentMethods.filter(
      (m) => m.category === "International Cards",
    ),
    "Domestic Card": paymentMethods.filter(
      (m) => m.category === "Domestic Card",
    ),
    "Mobile Wallets": paymentMethods.filter(
      (m) => m.category === "Mobile Wallets",
    ),
  };

  // Get methods for selected category
  const getCategoryMethods = () => {
    if (!selectedCategory) return [];
    const categoryMap = {
      international: "International Cards",
      domestic: "Domestic Card",
      mobile: "Mobile Wallets",
    };
    return groupedMethods[categoryMap[selectedCategory]] || [];
  };

  // Get category name by ID
  const getCategoryName = (id) => {
    const cat = paymentCategories.find((c) => c.id === id);
    return cat?.name || "";
  };

  const getSelectedMethod = () => {
    return paymentMethods.find((m) => m.id === selectedPaymentMethod);
  };

  // If we have payment payload, show the CyberSource form
  if (paymentPayload) {
    return (
      <div className="min-h-screen bg-gray-100">
        <main className="flex-1 p-8">
          <div className="max-w-lg mx-auto mt-8 p-8 bg-white rounded-2xl shadow-xl border border-green-100">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FontAwesomeIcon
                  icon={faLock}
                  className="w-10 h-10 text-green-600"
                />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Redirecting to Payment...
              </h1>
              <p className="text-gray-600 mb-6">
                You will be redirected to the payment gateway to complete your
                transaction securely.
              </p>
            </div>

            <form
              id="cybersource-form"
              method="post"
              action="https://testsecureacceptance.cybersource.com/pay"
              className="hidden"
            >
              {Object.entries(paymentPayload).map(([key, value]) => (
                <input
                  type="hidden"
                  name={key}
                  value={String(value)}
                  key={key}
                />
              ))}
            </form>

            <button
              type="button"
              onClick={() => {
                const form = document.getElementById("cybersource-form");
                if (form) {
                  form.submit();
                }
              }}
              className="w-full bg-green-600 text-white py-3 px-6 rounded-xl font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={faCreditCard} className="w-5 h-5" />
              Proceed to Payment Gateway
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Initial form to get payment payload
  return (
    <div className="min-h-screen bg-gray-100">
      <main className="flex-1">
        <div className="max-w-2xl mx-auto">
          {/* Payment Summary Card */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 mb-6 text-white">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-blue-100 text-sm font-medium">
                  Amount to Pay
                </p>
                <p className="text-3xl font-bold">
                  {formData.amount.toLocaleString()} MMK
                </p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <FontAwesomeIcon icon={faCreditCard} className="w-6 h-6" />
              </div>
            </div>
          </div>

          {/* Payment Method Selection - Two Layer Nested Dropdown */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FontAwesomeIcon
                icon={faGlobe}
                className="w-5 h-5 text-blue-600"
              />
              Select Payment Method
              <span className="text-red-500">*</span>
            </h2>

            <div className="space-y-4">
              {/* First Dropdown - Category Selection */}
              <div className="relative" ref={categoryDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. Select Category
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setCategoryDropdownOpen(!categoryDropdownOpen);
                    setMethodDropdownOpen(false);
                  }}
                  className={`w-full px-4 py-3 border-2 rounded-xl bg-white flex items-center justify-between transition-all duration-200 ${
                    selectedCategory
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {selectedCategory ? (
                      <>
                        <span className="text-2xl">
                          {
                            paymentCategories.find(
                              (c) => c.id === selectedCategory,
                            )?.icon
                          }
                        </span>
                        <span className="font-semibold text-gray-900">
                          {getCategoryName(selectedCategory)}
                        </span>
                      </>
                    ) : (
                      <span className="text-gray-500">Choose a category</span>
                    )}
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                      categoryDropdownOpen ? "rotate(180deg)" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* Category Dropdown Menu */}
                {categoryDropdownOpen && (
                  <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg">
                    {paymentCategories.map((category) => (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => {
                          setSelectedCategory(category.id);
                          setSelectedPaymentMethod(null);
                          setCategoryDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-blue-50 transition-colors ${
                          selectedCategory === category.id ? "bg-blue-50" : ""
                        }`}
                      >
                        <span className="text-2xl">{category.icon}</span>
                        <span className="font-medium text-gray-700">
                          {category.name}
                        </span>
                        {selectedCategory === category.id && (
                          <span className="ml-auto text-green-600">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Second Dropdown - Method Selection (shows only after category is selected) */}
              {selectedCategory && (
                <div className="relative" ref={methodDropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    2. Select Payment Method
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      if (!selectedCategory) return;
                      setMethodDropdownOpen(!methodDropdownOpen);
                      setCategoryDropdownOpen(false);
                    }}
                    disabled={!selectedCategory}
                    className={`w-full px-4 py-3 border-2 rounded-xl bg-white flex items-center justify-between transition-all duration-200 ${
                      selectedPaymentMethod
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    } ${!selectedCategory ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <div className="flex items-center gap-3">
                      {selectedPaymentMethod ? (
                        <>
                          <span className="text-2xl">
                            {getSelectedMethod()?.icon}
                          </span>
                          <span className="font-semibold text-gray-900">
                            {getSelectedMethod()?.name}
                          </span>
                        </>
                      ) : (
                        <span className="text-gray-500">
                          Choose {getCategoryName(selectedCategory)}
                        </span>
                      )}
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                        methodDropdownOpen ? "rotate(180deg)" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Method Dropdown Menu */}
                  {methodDropdownOpen && (
                    <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {getCategoryMethods().map((method) => (
                        <button
                          key={method.id}
                          type="button"
                          onClick={() => {
                            setSelectedPaymentMethod(method.id);
                            setMethodDropdownOpen(false);
                          }}
                          className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-blue-50 transition-colors ${
                            selectedPaymentMethod === method.id
                              ? "bg-blue-50"
                              : ""
                          }`}
                        >
                          <span className="text-2xl">{method.icon}</span>
                          <span className="font-medium text-gray-700">
                            {method.name}
                          </span>
                          {selectedPaymentMethod === method.id && (
                            <span className="ml-auto text-green-600">✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Error message if no payment method selected */}
            {!selectedPaymentMethod && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-700 text-sm flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="text-lg"
                  />
                  Please select a payment method to continue
                </p>
              </div>
            )}

            {/* Selected Payment Method Display */}
            {selectedPaymentMethod && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-green-700 font-medium flex items-center gap-2">
                  <FontAwesomeIcon icon={faCheck} className="text-green-600" />
                  Selected: {getSelectedMethod()?.name}
                </p>
              </div>
            )}
          </div>

          {/* Payment Details Form */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Payment Details
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency
                  </label>
                  <input
                    type="text"
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                    readOnly
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order Type
                  </label>
                  <input
                    type="text"
                    name="orderType"
                    value={formData.orderType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                    readOnly
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order ID
                  </label>
                  <input
                    type="number"
                    name="orderId"
                    value={formData.orderId}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !selectedPaymentMethod}
                className={`w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                  loading || !selectedPaymentMethod
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                <FontAwesomeIcon icon={faCreditCard} className="w-5 h-5" />
                {loading ? "Processing..." : "Proceed to Payment"}
              </button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Billing;
